import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button } from 'ui/button';
import { FormField } from 'ui/formField';
import { CHECKBOX_STATUS, Checkbox } from 'ui/checkbox';
import { InputText, TextArea } from 'ui/inputs';
import { Modal, ModalFooter, ModalBody, ModalHeader, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { spacing } from 'ui/theme';
import { METRICS_TAB } from '../constants';

export interface UpdateMetricDefinition {
  id: string;
  metricName: string;
  definition: string;
  longDefinition: string;
  calculation: string;
  learnMoreLink: string;
  isMIReportingMetric: boolean;
  isCAReportingMetric?: boolean;
}

interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UpdateMetricDefinition) => void;
  initialValues: UpdateMetricDefinition;
  selectedTab: string;
  isCreateModal: boolean;
}

const StyledFormField = styled(FormField)`
  margin-bottom: ${spacing.half};
`;

export const EditModal: React.FC<React.PropsWithChildren<EditModalProps>> = ({
  isOpen,
  onClose,
  onSubmit,
  initialValues,
  selectedTab,
  isCreateModal = false,
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<UpdateMetricDefinition>({
    defaultValues: initialValues,
  });

  const isAnalytics = selectedTab === METRICS_TAB.ANALYTICS;

  useEffect(() => {
    if (initialValues) {
      Object.entries(initialValues).forEach(([key, value]) => {
        setValue(key as keyof UpdateMetricDefinition, value);
      });
    }
  }, [initialValues, setValue]);

  const onSubmitForm = (data: UpdateMetricDefinition) => {
    onSubmit(data);
    onClose();
  };

  return (
    <Modal
      id="metric-definition-edit-modal"
      isModalOpen={isOpen}
      onClose={onClose}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader id="metric-definition-edit-modal-header" onClose={onClose}>
        {isCreateModal ? 'New' : 'Edit'} {isAnalytics ? 'analytics' : 'high level'} metric
        definition
      </ModalHeader>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <ModalBody id="metric-definition-edit-modal-body">
          <div>
            <StyledFormField
              errorText="This field is required"
              inputId="metricName"
              labelText="Metric name"
              hasError={!!errors.metricName}
            >
              <Controller
                name="metricName"
                control={control}
                rules={{ required: true }}
                render={({ field }) => <InputText {...field} hasError={!!errors.metricName} />}
              />
            </StyledFormField>
          </div>
          <div>
            <StyledFormField inputId="definition" labelText="Definition">
              <Controller
                name="definition"
                control={control}
                rules={{ required: false }}
                render={({ field }) => <TextArea {...field} hasError={!!errors.definition} />}
              />
            </StyledFormField>
          </div>
          <div>
            <StyledFormField inputId="longDefinition" labelText="Long definition">
              <Controller
                name="longDefinition"
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <TextArea {...field} hasError={!!errors.longDefinition} autoGrowMinHeight={80} />
                )}
              />
            </StyledFormField>
          </div>
          {/* Docs team doesn't want to use the calculation field - using definition field instead */}
          {/* <div>
            <StyledFormField
              inputId="calculation"
              labelText="Calculation"
            >
             <Controller
                name="calculation"
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <InputText
                    {...field}
                    hasError={!!errors.calculation}
                  />
                )}
              />
            </StyledFormField>
          </div> */}
          <div>
            <StyledFormField inputId="learnMoreLink" labelText="Learn more link">
              <Controller
                name="learnMoreLink"
                control={control}
                rules={{ required: false }}
                render={({ field }) => <InputText {...field} hasError={!!errors.learnMoreLink} />}
              />
            </StyledFormField>
          </div>
          <div>
            <StyledFormField inputId="isMIReportingMetric" labelText="Is shown in MI">
              <Controller
                name="isMIReportingMetric"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    hasError={!!errors.isMIReportingMetric}
                    status={field.value ? CHECKBOX_STATUS.CHECKED : CHECKBOX_STATUS.UNCHECKED}
                    onClick={() => field.onChange(!field.value)}
                  />
                )}
              />
            </StyledFormField>
          </div>
          {isAnalytics && (
            <StyledFormField inputId="isCAReportingMetric" labelText="Is shown in CA">
              <Controller
                name="isCAReportingMetric"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    hasError={!!errors.isCAReportingMetric}
                    status={field.value ? CHECKBOX_STATUS.CHECKED : CHECKBOX_STATUS.UNCHECKED}
                    onClick={() => field.onChange(!field.value)}
                  />
                )}
              />
            </StyledFormField>
          )}
        </ModalBody>
        <ModalFooter id="metric-definition-edit-modal-footer" onClose={onClose}>
          <Button type="submit" size={BUTTON_SIZES.LARGE} variant={BUTTON_VARIANTS.PRIMARY}>
            {isCreateModal ? 'Create' : 'Save'} {isAnalytics ? 'analytics' : 'high level'} metric
            definition
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
